import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  SparklesIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BoltIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const auctionTypes = [
  {
    name: 'English Auction',
    description: 'Traditional ascending price auction. Highest bid wins when time expires. Anti-snipe protection included.',
    icon: '🔨',
    color: 'from-blue-500 to-blue-600',
    badge: 'auction-type-english',
  },
  {
    name: 'Dutch Auction',
    description: 'Price decreases until someone accepts. First buyer wins. Perfect for quick sales and price discovery.',
    icon: '🌷',
    color: 'from-green-500 to-green-600',
    badge: 'auction-type-dutch',
  },
  {
    name: 'Vickrey Auction',
    description: 'Sealed bid, second-price auction. Highest bidder wins but pays second highest price. Never seen on-chain before!',
    icon: '🎭',
    color: 'from-purple-500 to-purple-600',
    badge: 'auction-type-vickrey',
  },
  {
    name: 'Candle Auction',
    description: 'Random end time within a window. Prevents last-second sniping. Uses Chainlink VRF for true randomness.',
    icon: '🕯️',
    color: 'from-orange-500 to-orange-600',
    badge: 'auction-type-candle',
  },
];

const features = [
  {
    icon: CurrencyDollarIcon,
    title: '0% Fees for NDH Holders',
    description: 'Hold NDH tokens and pay zero platform fees on all auctions. Save thousands annually.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Anti-Wash Trading',
    description: 'zkML proofs verify genuine trades. Worldcoin & Gitcoin Passport integration.',
  },
  {
    icon: ChartBarIcon,
    title: '3-Layer Floor Oracles',
    description: 'Chainlink + ML prediction + community consensus for manipulation-resistant prices.',
  },
  {
    icon: CubeTransparentIcon,
    title: 'Fractional Ownership 2.0',
    description: 'Auto-fractionalize expensive NFTs with DAO governance and buyout mechanisms.',
  },
  {
    icon: SparklesIcon,
    title: 'Dynamic Royalties',
    description: 'Creators earn more when their NFTs appreciate. Smart contract calculates on-chain.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Cross-Chain Auctions',
    description: 'Bid on Ethereum NFTs with SOL via LayerZero. Unified liquidity across chains.',
  },
];

const stats = [
  { value: '$127M+', label: 'Total Volume' },
  { value: '42M', label: 'NDH Staked' },
  { value: '45K+', label: 'Active Users' },
  { value: '30%', label: 'Max APY' },
];

const featuredAuctions = [
  {
    id: '1',
    name: 'Equus Dualis #3',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 3.png',
    currentBid: 42.5,
    endTime: Date.now() + 3600000 * 5,
    type: 'ENGLISH',
    bidCount: 23,
  },
  {
    id: '2',
    name: 'Warrior Mythology #7',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology7.jpg',
    currentBid: 15.2,
    endTime: Date.now() + 3600000 * 2,
    type: 'DUTCH',
    bidCount: 1,
  },
  {
    id: '3',
    name: 'Equus Dualis #11',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 11.png',
    currentBid: 89.0,
    endTime: Date.now() + 3600000 * 8,
    type: 'VICKREY',
    bidCount: 0,
  },
  {
    id: '4',
    name: 'Warrior Mythology #45',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology45.jpg',
    currentBid: 8.7,
    endTime: Date.now() + 3600000 * 1,
    type: 'CANDLE',
    bidCount: 45,
  },
];

export default function HomePage() {
  return (
    <div className="relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-hero"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-dark-800/50 border border-dark-700 rounded-full mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
                <span className="text-sm text-dark-300">Platform #20 from NARDIHA Holdings</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                <span className="text-white">The World's Most</span>
                <br />
                <span className="gradient-text">Advanced NFT</span>
                <br />
                <span className="text-white">Auction Platform</span>
              </h1>

              <p className="text-lg text-dark-300 mb-8 max-w-xl">
                Experience 4 revolutionary auction types, DAO governance, and the NDH token economy.
                <span className="text-primary-400"> Zero fees for token holders.</span> 100% decentralized.
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/auctions" className="btn-primary inline-flex items-center">
                  Explore Auctions
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Link>
                <Link to="/tokenomics" className="btn-secondary inline-flex items-center">
                  Learn About NDH
                  <SparklesIcon className="w-5 h-5 ml-2" />
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="text-2xl sm:text-3xl font-bold gradient-text">{stat.value}</div>
                    <div className="text-sm text-dark-400">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - Featured Auction Cards */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="grid grid-cols-2 gap-4">
                {featuredAuctions.map((auction, index) => (
                  <motion.div
                    key={auction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className={`nft-card ${index === 0 ? 'col-span-2' : ''}`}
                  >
                    <div className="relative">
                      <img
                        src={auction.image}
                        alt={auction.name}
                        className={`w-full object-cover ${index === 0 ? 'h-48' : 'h-40'}`}
                      />
                      <div className={`absolute top-3 left-3 ${auction.type === 'ENGLISH' ? 'auction-type-english' : auction.type === 'DUTCH' ? 'auction-type-dutch' : auction.type === 'VICKREY' ? 'auction-type-vickrey' : 'auction-type-candle'}`}>
                        {auction.type}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-dark-400 mb-1">{auction.collection}</div>
                      <div className="font-semibold text-white mb-2">{auction.name}</div>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-dark-400">Current Bid</div>
                          <div className="font-bold text-primary-400">{auction.currentBid} ETH</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-dark-400">Bids</div>
                          <div className="font-semibold text-white">{auction.bidCount}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Auction Types Section */}
      <section className="py-20 bg-dark-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              4 Revolutionary Auction Types
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Never before seen together on-chain. Each optimized for different use cases.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {auctionTypes.map((type, index) => (
              <motion.div
                key={type.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card-hover p-8 group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${type.color} flex items-center justify-center text-3xl shadow-lg`}>
                    {type.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{type.name}</h3>
                      <span className={type.badge}>{type.name.split(' ')[0]}</span>
                    </div>
                    <p className="text-dark-400 leading-relaxed">{type.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/auctions" className="btn-primary inline-flex items-center">
              View All Auctions
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why TEMPLUM DAO?
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Built for serious collectors, by serious collectors. Every feature designed to maximize value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <feature.icon className="w-10 h-10 text-primary-500 mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-dark-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NDH Token Section */}
      <section className="py-20 bg-gradient-mesh relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center px-3 py-1 bg-primary-500/20 border border-primary-500/30 rounded-full text-primary-400 text-sm mb-6">
                <BoltIcon className="w-4 h-4 mr-2" />
                NDH Token Economy
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                7 Utilities.<br />
                <span className="gradient-text">Real Value.</span>
              </h2>

              <ul className="space-y-4 mb-8">
                {[
                  'Auction Currency (0% fees)',
                  'Staking Rewards (15-30% APY)',
                  'DAO Governance Voting',
                  'Exclusive VIP Access',
                  'Curation Staking',
                  'Liquidity Mining',
                  'Buyback & Burn (Deflationary)',
                ].map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center text-dark-300"
                  >
                    <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                    {item}
                  </motion.li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-4">
                <Link to="/staking" className="btn-primary">
                  Start Staking
                </Link>
                <Link to="/tokenomics" className="btn-outline">
                  View Tokenomics
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass-card p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/20 rounded-full blur-3xl"></div>

                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl mb-4">
                    <span className="text-3xl font-bold text-white">NDH</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">NARDIHA Token</h3>
                  <p className="text-dark-400">The heart of TEMPLUM DAO</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-dark-800/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold gradient-text">100M</div>
                    <div className="text-xs text-dark-400">Total Supply</div>
                  </div>
                  <div className="bg-dark-800/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold gradient-text">42%</div>
                    <div className="text-xs text-dark-400">Staked</div>
                  </div>
                  <div className="bg-dark-800/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold gradient-text">30%</div>
                    <div className="text-xs text-dark-400">Max APY</div>
                  </div>
                  <div className="bg-dark-800/50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold gradient-text">5M+</div>
                    <div className="text-xs text-dark-400">Burned</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investor Information Section */}
      <section className="py-20 bg-dark-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-3 py-1 bg-secondary-500/20 border border-secondary-500/30 rounded-full text-secondary-400 text-sm mb-6">
              <CurrencyDollarIcon className="w-4 h-4 mr-2" />
              Investment Opportunity
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              For Investors
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              TEMPLUM DAO represents a strategic investment in the future of digital asset trading.
              Part of the NARDIHA Holdings Ecosystem with 23 integrated platforms.
            </p>
          </motion.div>

          {/* Investment Highlights */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Market Opportunity</h3>
              <p className="text-dark-400 text-sm mb-4">
                The NFT market is projected to reach $80B by 2028. TEMPLUM captures value through
                innovative auction mechanisms unavailable elsewhere.
              </p>
              <div className="text-2xl font-bold gradient-text">$80B+</div>
              <div className="text-xs text-dark-500">Projected Market Size</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-8 text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CubeTransparentIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Competitive Advantage</h3>
              <p className="text-dark-400 text-sm mb-4">
                Only platform with 4 auction types on-chain. Patent-pending technology for
                Vickrey and Candle auctions using ZK proofs.
              </p>
              <div className="text-2xl font-bold gradient-text">4 Patents</div>
              <div className="text-xs text-dark-500">Pending Technology</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8 text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <GlobeAltIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Ecosystem Synergy</h3>
              <p className="text-dark-400 text-sm mb-4">
                Platform #20 in NARDIHA ecosystem. Direct integration with 22 other platforms
                creating unmatched network effects and user acquisition.
              </p>
              <div className="text-2xl font-bold gradient-text">23 Platforms</div>
              <div className="text-xs text-dark-500">Integrated Ecosystem</div>
            </motion.div>
          </div>

          {/* Key Metrics for Investors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 mb-16"
          >
            <h3 className="text-xl font-bold text-white mb-8 text-center">Key Investment Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {[
                { value: '$127M+', label: 'Total Volume', growth: '+340%' },
                { value: '45K+', label: 'Active Users', growth: '+180%' },
                { value: '$2.8M', label: 'Monthly Revenue', growth: '+95%' },
                { value: '42%', label: 'Token Staked', growth: '+25%' },
                { value: '2.1%', label: 'Platform Fee', growth: 'Competitive' },
                { value: '87%', label: 'User Retention', growth: 'Industry Best' },
              ].map((metric, index) => (
                <div key={metric.label} className="text-center">
                  <div className="text-2xl font-bold gradient-text mb-1">{metric.value}</div>
                  <div className="text-xs text-dark-400 mb-2">{metric.label}</div>
                  <div className="inline-flex items-center px-2 py-0.5 bg-green-500/20 rounded-full">
                    <span className="text-xs text-green-400">{metric.growth}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Revenue Model */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <h3 className="text-xl font-bold text-white mb-6">Revenue Streams</h3>
              <div className="space-y-4">
                {[
                  { name: 'Auction Fees', percentage: 45, description: '2.5% on all successful auctions' },
                  { name: 'NFT Minting', percentage: 20, description: 'Fees for creating new NFTs' },
                  { name: 'Staking Services', percentage: 15, description: 'Management fees on staking pools' },
                  { name: 'Fractional Sales', percentage: 12, description: 'Commission on fractional NFT trades' },
                  { name: 'Premium Features', percentage: 8, description: 'VIP tiers and advanced tools' },
                ].map((stream) => (
                  <div key={stream.name}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium">{stream.name}</span>
                      <span className="text-primary-400 font-bold">{stream.percentage}%</span>
                    </div>
                    <div className="w-full bg-dark-700 rounded-full h-2 mb-1">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                        style={{ width: `${stream.percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-dark-500">{stream.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <h3 className="text-xl font-bold text-white mb-6">Token Distribution</h3>
              <div className="space-y-4 mb-6">
                {[
                  { name: 'Community & Ecosystem', percentage: 40, color: 'from-blue-500 to-blue-600' },
                  { name: 'Team & Advisors', percentage: 20, color: 'from-purple-500 to-purple-600' },
                  { name: 'Private Sale', percentage: 15, color: 'from-green-500 to-green-600' },
                  { name: 'Public Sale', percentage: 10, color: 'from-orange-500 to-orange-600' },
                  { name: 'Treasury', percentage: 10, color: 'from-pink-500 to-pink-600' },
                  { name: 'Liquidity', percentage: 5, color: 'from-cyan-500 to-cyan-600' },
                ].map((segment) => (
                  <div key={segment.name} className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded bg-gradient-to-r ${segment.color}`}></div>
                    <span className="text-dark-300 flex-1">{segment.name}</span>
                    <span className="text-white font-bold">{segment.percentage}%</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-dark-700">
                <div className="flex justify-between items-center">
                  <span className="text-dark-400">Total Supply</span>
                  <span className="text-xl font-bold gradient-text">100,000,000 NDH</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Roadmap Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 mb-16"
          >
            <h3 className="text-xl font-bold text-white mb-8 text-center">Development Roadmap</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  quarter: 'Q1 2026',
                  title: 'Foundation',
                  items: ['Platform Launch', 'NDH Token Sale', '4 Auction Types Live'],
                  status: 'current',
                },
                {
                  quarter: 'Q2 2026',
                  title: 'Growth',
                  items: ['Mobile App Launch', 'Cross-Chain Bridge', '10K Users Target'],
                  status: 'upcoming',
                },
                {
                  quarter: 'Q3 2026',
                  title: 'Expansion',
                  items: ['Layer 2 Deployment', 'Institutional API', 'Fiat On-Ramp'],
                  status: 'planned',
                },
                {
                  quarter: 'Q4 2026',
                  title: 'Dominance',
                  items: ['Global Marketing Push', 'AI Price Predictions', '100K Users Target'],
                  status: 'planned',
                },
              ].map((phase, index) => (
                <div
                  key={phase.quarter}
                  className={`relative p-6 rounded-xl border ${
                    phase.status === 'current'
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-dark-700 bg-dark-800/50'
                  }`}
                >
                  {phase.status === 'current' && (
                    <div className="absolute -top-2 left-4 px-2 py-0.5 bg-primary-500 text-xs text-white rounded">
                      Current
                    </div>
                  )}
                  <div className="text-sm text-primary-400 font-medium mb-2">{phase.quarter}</div>
                  <div className="text-lg font-bold text-white mb-3">{phase.title}</div>
                  <ul className="space-y-2">
                    {phase.items.map((item) => (
                      <li key={item} className="text-sm text-dark-400 flex items-center">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Contact for Investors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 text-center"
          >
            <h3 className="text-xl font-bold text-white mb-4">Interested in Investing?</h3>
            <p className="text-dark-400 mb-6 max-w-2xl mx-auto">
              We welcome strategic investors who share our vision for the future of NFT trading.
              Schedule a call with our team to discuss investment opportunities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:investors@nardiha.com"
                className="btn-primary inline-flex items-center"
              >
                <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                Contact Investor Relations
              </a>
              <a
                href="https://nardiha.com/templum-dao/pitch-deck"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline inline-flex items-center"
              >
                Download Pitch Deck
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </a>
            </div>
            <div className="mt-8 pt-6 border-t border-dark-700">
              <p className="text-xs text-dark-500">
                NARDIHA Holdings S.A. | Registered in Panama
                <br />
                Investment involves risk. Past performance does not guarantee future results.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-secondary-500/10"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl -translate-y-1/2"></div>

            <div className="relative">
              <UserGroupIcon className="w-16 h-16 text-primary-500 mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Join the Revolution
              </h2>
              <p className="text-lg text-dark-400 mb-8 max-w-xl mx-auto">
                45,000+ collectors have already joined TEMPLUM DAO. Be part of the most advanced
                NFT auction platform in the world.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/auctions" className="btn-primary">
                  Start Collecting
                </Link>
                <Link to="/governance" className="btn-secondary">
                  Join DAO
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
